import AppBanner from '@/components/AppBanner'
import BANNER_TEXT_VALUES from '@/components/AppBanner/constants'
import Cobrand from '@/components/Cobrand'
import Home from '@/components/Home'
import Layout from '@/components/Layout'
import Meta from '@/components/Meta'
import TmsScript from '@/components/TmsScript/TmsScript'
import DEFAULT_META_TAGS from '@/constants/meta'
import { HOME_PRODUCT_TYPE } from '@/constants/products'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import usePageTitleForHotels from '@/hooks/usePageTitleForHotels'
import type { ContentPageProps, CustomContext } from '@/types'
import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import newrelic from 'newrelic'
import { type GetServerSideProps } from 'next'
import { useState } from 'react'
import setiService from '../server/middleware/seti'
import prePopulateHotelValues from '../server/prepop/hotels'
import getContentfulData from '../server/utils/get-contentful-data'
import getSkymondeData from '../server/utils/get-skymonde-data'
import oktaJwtVerify from '../server/utils/okta-jwt-verifier'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import retrieveContentfulSecrets from '../server/utils/retrieve-contentful-secrets'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import mapSetiResponse from '../utils/map-seti-response'

function Homepage({ headerHTML, footerHTML, bootstrapData, content }: ContentPageProps) {
  const pageTitle = usePageTitleForHotels(DEFAULT_META_TAGS.title.home)
  const canonicalUrl = useCanonicalUrl()
  const [queryClient] = useState(() => new QueryClient())
  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.home}
        pageTitle={pageTitle}
        canonicalUrl={canonicalUrl}
      />
      <TmsScript product='home' />
      <FeatureProvider features={bootstrapData.featureToggles}>
        <ContentfulProvider contentful={content}>
          <QueryClientProvider client={queryClient}>
            <AppBanner content={BANNER_TEXT_VALUES[HOME_PRODUCT_TYPE]} />
            <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
              <Cobrand />
              <Home />
            </Layout>
          </QueryClientProvider>
        </ContentfulProvider>
      </FeatureProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (ctx) => {
  return newrelic.startSegment('Next/GSSP/Homepage', true, async () => {
    const { req, res } = ctx as CustomContext
    const bootstrapData = generateBootstrapData({ product: 'home', req, res })
    const { webstats, referral, appVersion, locale } = bootstrapData
    const { clientGUID: cguid } = webstats
    const { contentfulDeliverySecret, contentfulPreviewSecret } = retrieveContentfulSecrets()
    const isContentfulEnabled = res.locals.features?.siteExperience.isContentfulEnabled ?? true
    const isSkymondeEnabled = res.locals.features?.siteExperience?.isSkymondeEnabled ?? false
    const [
      signInStatus,
      { content, experimentNames },
      hotelPrePopData,
      pclnHeaderFooterContent,
      skymondeSEO,
    ] = await Promise.all([
      oktaJwtVerify({ authHdr: req.headers.authorization ?? '', cguid }),
      getContentfulData({
        contentTypeId: 'pageHome',
        contentfulDeliverySecret,
        contentfulPreviewSecret,
        contentPreview: ctx.query['content-preview'] === 'true',
        isContentfulEnabled,
      }),
      prePopulateHotelValues(ctx.query),
      fetchPCLNHeaderFooterContent(req, res),
      getSkymondeData('home', locale.localeCode, isSkymondeEnabled),
    ])

    const setiResponseEvents = await setiService(req, {
      appVersion,
      allowSetiOverrides: true,
      webstats,
      referral,
      reqQueryParams: ctx.query,
      ...(experimentNames && {
        additionalExperiments: experimentNames,
      }),
    })
    const prePopData = hotelPrePopData ? { hotels: hotelPrePopData } : null

    const bootstrapWithJwtSignInData = {
      ...bootstrapData,
      customer: {
        signedIn: signInStatus.signedIn ?? false,
        emailAddress: signInStatus.email ?? '',
        firstName: String(signInStatus.firstName ?? ''),
      },
    }

    return {
      props: {
        ...pclnHeaderFooterContent,
        content,
        bootstrapData: {
          ...bootstrapWithJwtSignInData,
          fullSETI: setiResponseEvents,
          seti: mapSetiResponse(setiResponseEvents),
          signInStatus,
          ...(prePopData && { prePopData }),
          skymondeSEO,
        },
      },
    }
  })
}

export default Homepage
